// Parsowanie listy wklejonej z SMS/komunikatora: jedna linia = jedna pozycja.

export interface ParsedLine {
  raw: string;
  name: string;
  qty: number | null;
  unit: 'szt' | 'opak' | null;
  amount: { value: number; unit: 'g' | 'kg' | 'ml' | 'l' } | null;
  /** Uwagi wycięte z nazwy: nawiasy, alternatywy („lub zwykła”), dopiski po myślniku lub przecinku. */
  note: string | null;
}

type Amount = NonNullable<ParsedLine['amount']>;

interface Quantities {
  qty: number | null;
  unit: ParsedLine['unit'];
  amount: Amount | null;
  /** Tekst po wycięciu ilości. */
  rest: string;
}

// Granice słów zgodne z Unicode — \b w JS nie zna polskich liter. Drugi koniec zakresu „2-3” to nie ilość.
const START = String.raw`(?<![\p{L}\p{N}])(?<!\d[.,])(?<!\d\s*[-–]\s*)`;
const END = String.raw`(?![\p{L}\p{N}])`;
const NUM = String.raw`\d+(?:[.,]\d+)?`;

const AMOUNT_UNITS: readonly { alias: string; unit: Amount['unit']; factor: number }[] = [
  { alias: 'kilogram(?:y|ów|ow|a)?|kilo|kg', unit: 'kg', factor: 1 },
  { alias: 'dekagram(?:y|ów|ow|a)?|deko|deka|dkg|dag', unit: 'g', factor: 10 },
  { alias: 'gram(?:y|ów|ow|a)?|gr|g', unit: 'g', factor: 1 },
  { alias: 'mililitr(?:y|ów|ow|a)?|ml', unit: 'ml', factor: 1 },
  { alias: 'litr(?:y|ów|ow|a)?|l', unit: 'l', factor: 1 },
];
const UNIT_MATCHERS = AMOUNT_UNITS.map((u) => ({ ...u, re: new RegExp(`^(?:${u.alias})$`, 'iu') }));
const UNITS = AMOUNT_UNITS.map((u) => u.alias).join('|');

/** „6x1,5l”, „4 x 150 g” — liczba sztuk razy gramatura. */
const PACK_RE = new RegExp(`${START}(\\d+)\\s*[x×*]\\s*(${NUM})\\s*(${UNITS})${END}\\.?`, 'iu');
/** „500 g”, „0,5kg”, „pół litra”. */
const AMOUNT_RE = new RegExp(`${START}(?:(${NUM})\\s*|(pół|pol)\\s+)(${UNITS})${END}\\.?`, 'iu');

const SZT = 'sztuk[aięy]?|szt';
const OPAK = 'opakowa(?:nia|nie|ń|n)|opak|opk|op';
const TIMES = String.raw`(?<![\p{L}\p{N}])[x×*]\s*(\d+)`;

const COUNT_PATTERNS: readonly { re: RegExp; unit: ParsedLine['unit'] }[] = [
  // „x3szt”, „x 1 op.”
  { re: new RegExp(`${TIMES}\\s*(?:${SZT})${END}\\.?`, 'iu'), unit: 'szt' },
  { re: new RegExp(`${TIMES}\\s*(?:${OPAK})${END}\\.?`, 'iu'), unit: 'opak' },
  { re: new RegExp(`${START}(\\d+)\\s*(?:${SZT})${END}\\.?`, 'iu'), unit: 'szt' },
  { re: new RegExp(`${START}(\\d+)\\s*(?:${OPAK})${END}\\.?`, 'iu'), unit: 'opak' },
  { re: new RegExp(`${START}(\\d+)\\s*[x×*]${END}`, 'iu'), unit: null },
  { re: new RegExp(`${TIMES}${END}(?![.,]\\d)`, 'iu'), unit: null },
  // „3 jogurty” — ale nie „500 g sera”
  { re: new RegExp(`^(\\d+)\\s+(?!(?:${UNITS})${END})(?=\\p{L})`, 'iu'), unit: null },
];
/** „Winogrona op.” — opakowanie bez liczby to jedno opakowanie. */
const BARE_PACK_RE = new RegExp(`(?<![\\p{L}\\p{N}])(?:opakowanie|opak|op)${END}\\.?`, 'iu');

/** „[lista 1]” — nagłówek, nie pozycja. */
const HEADER_RE = /^\[[^\]]*\]$/u;
const KEYWORDS = String.raw`lub|albo|ewentualnie|ew\.|np\.|najlepiej`;
/**
 * Początek ogona z alternatywą lub warunkiem. Ilości w nim zostają w uwadze („lub 10 bułek”).
 * „do” tylko przed datą („Praska do czosnku” to nazwa), „i” tylko przed liczbą („i 5 bułek”).
 */
const TAIL_RE = new RegExp(String.raw`\s(?:${KEYWORDS}|do(?=\s+\d{1,2}[./]\d{1,2})|i(?=\s+\d))(?=\s|$)`, 'giu');
const LEADING_KEYWORD_RE = new RegExp(String.raw`^(?:${KEYWORDS}|do|i)(?=\s|$)`, 'iu');
// Dopiski w linii: „ - ”, „ – ”, „!”, „;” i przecinek (nie dziesiętny).
const REMARK_SEPARATOR_RE = /\s[-–—]+(?=\s|$)|^[-–—]+(?=\s)|[!;]|(?<!\d),|,(?!\d)/u;

// Nawiasy zastępujemy znakami z Private Use Area, żeby ich treść nie wpływała na podział linii.
const MASK_BASE = 0xe000;
const MASK_CHARS = `${String.fromCharCode(MASK_BASE)}-${String.fromCharCode(0xf8ff)}`;
const MASK_RE = new RegExp(`[${MASK_CHARS}]`, 'gu');

/** „bułki 6” — liczba 1–99 na końcu nazwy (nawiasy po niej nie przeszkadzają). */
const TRAILING_COUNT_RE = new RegExp(`\\s([1-9]\\d?)(?=[\\s${MASK_CHARS}]*$)`, 'u');
// Po tych słowach liczba to rozmiar/wariant, nie ilość („pieluchy rozmiar 4”, „Omega 3”).
const NOT_COUNT_WORDS = new Set(['rozmiar', 'rozm', 'r', 'nr', 'numer', 'typ', 'model', 'omega', 'faza', 'etap']);

const BULLET_RE =
  /^(?:[-*•·–—▪▫◦●○►▸✔✓☑✅☐+>]\uFE0F?|\[\s*[xX✓✔]?\s*\]|\d+[.)](?!\d)|\d+\s*[-–—](?=\s))\s*/u;

// Rozdziela jednolinijkową listę, ale nie „1,5 kg”.
const INLINE_SEPARATOR_RE = /;|(?<!\d),|,(?!\d)/;

export function parseList(text: string): ParsedLine[] {
  const lines = text
    .split(/\r\n|\r|\n/)
    .map((l) => l.trim())
    .filter((l) => l !== '');
  const [first] = lines;
  const items = lines.length === 1 && first !== undefined && /[,;]/.test(first) ? first.split(INLINE_SEPARATOR_RE) : lines;
  return items.map(parseLine).filter((p): p is ParsedLine => p !== null);
}

export function parseLine(line: string): ParsedLine | null {
  const raw = line.trim();
  // pusta linia, same emoji, interpunkcja lub numeracja, nagłówek listy
  if (!/\p{L}/u.test(raw) || HEADER_RE.test(raw)) return null;

  const stripped = stripBullets(raw.replace(MASK_RE, ''));
  const { masked, groups } = maskGroups(stripped);

  // Ilości bierzemy z całej linii poza ogonem z alternatywą.
  const tailAt = findTail(masked);
  const main = takeQuantities(tailAt === null ? masked : masked.slice(0, tailAt));
  let { qty, unit, amount } = main;

  // „(10 szt)” — nawias z samą ilością uzupełnia brakującą ilość.
  for (const g of groups) {
    const q = takeQuantities(g.text);
    const pure = (q.qty !== null || q.amount !== null) && !/[\p{L}\p{N}]/u.test(q.rest);
    if (!pure || (q.qty !== null && qty !== null) || (q.amount !== null && amount !== null)) continue;
    if (q.qty !== null) {
      qty = q.qty;
      unit = q.unit;
    }
    amount ??= q.amount;
    g.used = true;
  }

  const parts = main.rest.split(REMARK_SEPARATOR_RE);
  const headAt = parts.findIndex((p) => /\p{L}/u.test(p));
  if (headAt === -1) return { raw, name: collapseSpaces(stripped), qty, unit, amount, note: null };

  let head = parts[headAt] ?? '';
  if (qty === null) {
    const m = TRAILING_COUNT_RE.exec(head);
    if (m && isCountContext(head.slice(0, m.index))) {
      qty = Number(m[1]);
      head = `${head.slice(0, m.index)} ${head.slice(m.index + m[0].length)}`;
    }
  }

  // Nawiasy z nazwy to osobne uwagi, w dopiskach zostają na swoim miejscu.
  const notes: string[] = [];
  const name = cleanName(
    head.replace(MASK_RE, (c) => {
      const text = groupText(groups, c);
      if (text !== null) notes.push(text);
      return ' ';
    }),
  );
  const tail = tailAt === null ? [] : masked.slice(tailAt).split(REMARK_SEPARATOR_RE);
  notes.push(...remarkNotes([...parts.slice(0, headAt), ...parts.slice(headAt + 1), ...tail], groups));

  return { raw, name, qty, unit, amount, note: notes.length > 0 ? notes.join('; ') : null };
}

/**
 * Pozycja z powrotem jako linia listy, np. „woda 1,5 l x6 (gazowana)” — do edycji i kopiowania list.
 * parseLine odczytuje z niej te same pola. Uwaga z nawiasami idzie po myślniku, bo nawiasy się nie zagnieżdżają.
 */
export function formatLine(p: Pick<ParsedLine, 'name' | 'qty' | 'unit' | 'amount' | 'note'>): string {
  const parts = [p.name];
  if (p.amount) parts.push(`${formatNum(p.amount.value)} ${p.amount.unit}`);
  if (p.qty !== null) parts.push(p.unit === null ? `x${formatNum(p.qty)}` : `${formatNum(p.qty)} ${p.unit}.`);
  if (p.note) parts.push(/[()]/u.test(p.note) ? `- ${p.note}` : `(${p.note})`);
  return parts.join(' ');
}

const formatNum = (n: number) => String(n).replace('.', ',');

interface Group {
  text: string;
  /** Treść zużyta jako ilość. */
  used: boolean;
}

function maskGroups(s: string): { masked: string; groups: Group[] } {
  const groups: Group[] = [];
  const masked = s
    .replace(/\(([^()]*)(?:\)|$)/gu, (_, inner: string) => {
      groups.push({ text: collapseSpaces(inner), used: false });
      return ` ${String.fromCharCode(MASK_BASE + groups.length - 1)} `;
    })
    .replace(/[()]/gu, ' ');
  return { masked, groups };
}

function groupText(groups: readonly Group[], mask: string): string | null {
  const g = groups[mask.charCodeAt(0) - MASK_BASE];
  return g && !g.used && g.text !== '' ? g.text : null;
}

/**
 * Czy liczba po tym tekście to ilość. Poprzedzające słowo musi mieć litery. Zostawiamy w nazwie
 * kody/modele z 1–2 wielkich liter („WD 40”, także „baterie AA 4”) i rozmiary („rozmiar 4”).
 */
function isCountContext(before: string): boolean {
  const word = before.replace(MASK_RE, ' ').trim().split(/\s+/u).at(-1) ?? '';
  if (!/\p{L}/u.test(word) || /^\p{Lu}{1,2}$/u.test(word)) return false;
  return !NOT_COUNT_WORDS.has(word.toLowerCase().replace(/\.$/u, ''));
}

function findTail(s: string): number | null {
  for (const m of s.matchAll(TAIL_RE)) {
    if (/\p{L}/u.test(s.slice(0, m.index))) return m.index + 1;
  }
  return null;
}

/** Dopiski po separatorach. Samotne „LUB” (np. przed „!”) łączy się z następną częścią. */
function remarkNotes(parts: readonly string[], groups: readonly Group[]): string[] {
  const notes: string[] = [];
  let pending = '';
  for (const part of parts) {
    const restored = part.replace(MASK_RE, (c) => {
      const text = groupText(groups, c);
      return text === null ? ' ' : ` (${text}) `;
    });
    let text = cleanNote(restored).replace(LEADING_KEYWORD_RE, (k) => k.toLowerCase());
    if (text === '') continue;
    if (pending !== '') {
      text = `${pending} ${text}`;
      pending = '';
    }
    if (LEADING_KEYWORD_RE.exec(text)?.[0] === text) pending = text;
    else notes.push(text);
  }
  return notes;
}

function takeQuantities(text: string): Quantities {
  let rest = text.trim();
  let qty: number | null = null;
  let unit: ParsedLine['unit'] = null;
  let amount: Amount | null = null;

  const cut = (m: RegExpExecArray): void => {
    rest = `${rest.slice(0, m.index)} ${rest.slice(m.index + m[0].length)}`.trim();
  };

  const pack = PACK_RE.exec(rest);
  if (pack) {
    qty = Number(pack[1]);
    amount = toAmount(pack[2], pack[3]);
    cut(pack);
  } else {
    const m = AMOUNT_RE.exec(rest);
    if (m) {
      amount = toAmount(m[1] ?? '0.5', m[3]);
      cut(m);
    }
  }

  if (qty === null) {
    for (const pattern of COUNT_PATTERNS) {
      const m = pattern.re.exec(rest);
      if (!m) continue;
      qty = Number(m[1]);
      unit = pattern.unit;
      cut(m);
      break;
    }
  }

  if (unit === null) {
    const m = BARE_PACK_RE.exec(rest);
    if (m) {
      qty ??= 1;
      unit = 'opak';
      cut(m);
    }
  }

  return { qty, unit, amount, rest };
}

function stripBullets(line: string): string {
  let s = line;
  for (let m = BULLET_RE.exec(s); m; m = BULLET_RE.exec(s)) s = s.slice(m[0].length);
  return s;
}

function toAmount(num: string | undefined, unitWord: string | undefined): Amount | null {
  const def = UNIT_MATCHERS.find((u) => u.re.test(unitWord ?? ''));
  const value = Number((num ?? '').replace(',', '.'));
  if (!def || !Number.isFinite(value)) return null;
  return { value: Math.round(value * def.factor * 1000) / 1000, unit: def.unit };
}

function cleanName(s: string): string {
  return collapseSpaces(s)
    .replace(/^[^\p{L}\p{N}„"'[]+/u, '')
    .replace(/[^\p{L}\p{N}%\]"'”]+$/u, '');
}

function cleanNote(s: string): string {
  return collapseSpaces(s)
    .replace(/^[^\p{L}\p{N}(„"'[]+/u, '')
    .replace(/[^\p{L}\p{N}%.)\]"'”]+$/u, '');
}

function collapseSpaces(s: string): string {
  return s.replace(/\s+/gu, ' ').trim();
}
